import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../utils/api';

interface Slot {
  dayOfWeek: number;
  startTime: string;
  endTime: string;
}

const DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAY_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
// Monday-first ordering used throughout the EJL UI.
const WEEK_ORDER = [1, 2, 3, 4, 5, 6, 0];

function formatTime(t: string): string {
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'pm' : 'am';
  const h12 = h % 12 || 12;
  return m === 0 ? `${h12}${ampm}` : `${h12}:${String(m).padStart(2, '0')}${ampm}`;
}

function formatRange(start: string, end: string): string {
  const s = formatTime(start);
  const e = formatTime(end);
  const sAmPm = s.slice(-2);
  const eAmPm = e.slice(-2);
  // "3–8pm" rather than "3pm–8pm" when both ends share am/pm.
  if (sAmPm === eAmPm) return `${s.slice(0, -2)}–${e}`;
  return `${s}–${e}`;
}

interface Group {
  days: number[];
  ranges: string[];
}

function groupBySlots(slots: Slot[]): Group[] {
  const byDay = new Map<number, string[]>();
  for (const s of slots) {
    const list = byDay.get(s.dayOfWeek) ?? [];
    list.push(formatRange(s.startTime, s.endTime));
    byDay.set(s.dayOfWeek, list);
  }
  const ordered = WEEK_ORDER
    .filter(d => byDay.has(d))
    .map(d => ({ day: d, ranges: [...byDay.get(d)!].sort() }));

  const result: Group[] = [];
  for (const entry of ordered) {
    const last = result[result.length - 1];
    const sameRanges =
      last &&
      last.ranges.length === entry.ranges.length &&
      last.ranges.every((r, i) => r === entry.ranges[i]);
    const consecutive =
      last &&
      WEEK_ORDER.indexOf(entry.day) ===
        WEEK_ORDER.indexOf(last.days[last.days.length - 1]) + 1;
    if (sameRanges && consecutive) {
      last!.days.push(entry.day);
    } else {
      result.push({ days: [entry.day], ranges: entry.ranges });
    }
  }
  return result;
}

function formatDayRange(days: number[], short: boolean): string {
  const names = short ? DAY_SHORT : DAY_LONG;
  if (days.length === 1) return names[days[0]];
  return `${names[days[0]]}–${names[days[days.length - 1]]}`;
}

interface Props {
  variant?: 'footer' | 'panel';
  className?: string;
}

const AvailabilitySummary: React.FC<Props> = ({ variant = 'footer', className = '' }) => {
  const [slots, setSlots] = useState<Slot[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get('/availability/public')
      .then(res => {
        if (cancelled) return;
        setSlots(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, []);

  const isFooter = variant === 'footer';

  if (slots === null && !failed) {
    if (isFooter) {
      return (
        <ul className={`space-y-2 ${className}`} aria-busy="true">
          {[0, 1, 2].map(i => (
            <li key={i} className="h-4 rounded bg-cream/10 animate-pulse" style={{ width: `${70 - i * 8}%` }} />
          ))}
        </ul>
      );
    }
    return (
      <div className={`border border-rule rounded-card p-6 md:p-7 ${className}`} aria-busy="true">
        <div className="h-3 w-24 bg-ink/10 rounded animate-pulse" />
        <div className="mt-5 space-y-3">
          {[0, 1, 2, 3].map(i => (
            <div key={i} className="h-4 bg-ink/10 rounded animate-pulse" style={{ width: `${80 - i * 10}%` }} />
          ))}
        </div>
      </div>
    );
  }

  const groups = !failed && slots && slots.length > 0 ? groupBySlots(slots) : [];
  const empty = groups.length === 0;

  if (isFooter) {
    return (
      <div className={className}>
        {empty ? (
          <p className="text-[15px] text-cream/80 leading-relaxed">
            Weekday evenings and Saturday mornings. Book the £5 20-minute intro call.
          </p>
        ) : (
          <ul className="space-y-1.5">
            {groups.map((g, i) => (
              <li key={i} className="flex items-baseline gap-3 text-[15px]">
                <span className="font-serif text-cream min-w-[4.5rem] shrink-0">
                  {formatDayRange(g.days, true)}
                </span>
                <span className="text-cream/70">{g.ranges.join(', ')}</span>
              </li>
            ))}
          </ul>
        )}
        <p className="mt-4 text-[13px] text-cream/60">
          UK time. Book the{' '}
          <Link
            to="/contact"
            className="text-cream/80 underline decoration-ochre/60 underline-offset-4 hover:text-ochre"
          >
            £5 20-minute intro call
          </Link>.
        </p>
      </div>
    );
  }

  return (
    <div className={`border border-rule rounded-card p-6 md:p-7 bg-paper ${className}`}>
      <div className="flex items-baseline justify-between gap-4">
        <p className="eyebrow">Availability</p>
        <Link to="/contact" className="text-sm text-ink/70 hover:text-ochre no-underline">
          Book a call <span aria-hidden="true">→</span>
        </Link>
      </div>
      {empty ? (
        <p className="mt-5 text-body text-ink/80">
          Schedule varies — ask for current availability.
        </p>
      ) : (
        <ul className="mt-4 divide-y divide-rule">
          {groups.map((g, i) => (
            <li key={i} className="flex items-baseline justify-between gap-6 py-3">
              <span className="font-serif text-ink">{formatDayRange(g.days, false)}</span>
              <span className="text-ink/75 text-sm tabular-nums">{g.ranges.join(', ')}</span>
            </li>
          ))}
        </ul>
      )}
      <p className="mt-5 text-xs text-muted">All times UK. Set by Ethan; updated when teaching commitments shift.</p>
    </div>
  );
};

export default AvailabilitySummary;
